// ACADEMIC_EVENTS (calendário: feriados, datas do semestre) e SLOT_DEFS (horário dos
// blocos de aula) são os mesmos pra todo mundo, não importa o período — por isso ficam
// globais aqui, em vez de dentro de cada dataset de período.
import { ACADEMIC_EVENTS, SLOT_DEFS } from "./data.js";

const DAY_MS = 86_400_000;

export function parseISODate(isoDate) {
  const [year, month, day] = String(isoDate).split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function dateToISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(date, amount) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
}

function calendarDaysBetween(startISO, endISO) {
  const start = parseISODate(startISO);
  const end = parseISODate(endISO);
  return Math.round((Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / DAY_MS);
}

export function timeToMinutes(value) {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(value))) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function validDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value)) && dateToISO(parseISODate(value)) === value;
}

function extraEventsForDay(date, profile) {
  const iso = dateToISO(date);
  return profile.extraActivities.filter((item) => item.weekday === date.getDay() && iso >= item.startDate && iso <= item.endDate)
    .map((item) => ({ ...item, code: item.kind === "elective" ? "OPTATIVA" : "EXTENSÃO", source: "personal",
      startMinutes: timeToMinutes(item.start), endMinutes: timeToMinutes(item.end), period: "custom", slotIds: [] }));
}

function combineEvents(events, extras) {
  const combined = [...events, ...extras].sort((a, b) => a.startMinutes - b.startMinutes || (a.endMinutes || a.startMinutes) - (b.endMinutes || b.startMinutes));
  return combined.map((event, index) => ({ ...event, overlaps: combined.filter((other, otherIndex) => otherIndex !== index &&
    Number.isFinite(event.endMinutes) && Number.isFinite(other.endMinutes) && event.startMinutes < other.endMinutes && other.startMinutes < event.endMinutes).map((other) => other.title) }));
}

export function getSyndromeForProfile(profile, blockNumber) {
  return profile.rotation?.[blockNumber] || null;
}

function isSameEvent(first, second) {
  if (!first || !second) return false;
  if (first.kind === "conflict" || second.kind === "conflict") return false;
  return ["code", "title", "professor", "location", "regional", "kind", "source"].every((key) => (first[key] || "") === (second[key] || ""));
}

function createConflict(events) {
  return {
    code: "ATENÇÃO",
    title: "Conflito na configuração",
    professor: "Revise os grupos escolhidos",
    location: "",
    kind: "conflict",
    source: "profile",
    candidates: events
  };
}

function resolveSlot(baseEvent, overlays) {
  if (overlays.length > 1) {
    const candidates = baseEvent && baseEvent.kind !== "placeholder" && baseEvent.kind !== "tp" ? [baseEvent, ...overlays] : overlays;
    return createConflict(candidates);
  }

  if (overlays.length === 1) {
    const overlay = overlays[0];
    if (!baseEvent || baseEvent.kind === "placeholder" || baseEvent.kind === "tp") return overlay;
    if (isSameEvent(baseEvent, overlay)) return overlay;
    return createConflict([baseEvent, overlay]);
  }

  if (!baseEvent) return null;
  if (baseEvent.kind === "placeholder" || baseEvent.kind === "tp") return null;
  return baseEvent;
}

function attachSlot(event, slot) {
  if (!event) return null;
  return {
    ...event,
    slotIds: [slot.id],
    start: slot.start,
    end: slot.end,
    startMinutes: slot.startMinutes,
    endMinutes: slot.endMinutes,
    period: slot.period
  };
}

export function mergeConsecutiveEvents(events) {
  const merged = [];
  for (const event of events.filter(Boolean)) {
    const previous = merged.at(-1);
    if (previous && previous.period === event.period && isSameEvent(previous, event)) {
      previous.end = event.end;
      previous.endMinutes = event.endMinutes;
      previous.slotIds.push(...event.slotIds);
    } else {
      merged.push({ ...event, slotIds: [...event.slotIds] });
    }
  }
  return merged;
}

function specialDaySchedule(date, notice, block, syndrome) {
  const event = {
    code: notice.noClasses ? "SEM AULA" : "CALENDÁRIO",
    title: notice.title,
    professor: notice.subtitle || "",
    location: "",
    kind: notice.kind === "holiday" ? "holiday" : "notice",
    source: "calendar",
    start: "",
    end: "",
    startMinutes: null,
    endMinutes: null,
    period: "day",
    slotIds: []
  };
  return { date, block, syndrome, group: null, notice, events: [event], dataWarning: "" };
}

export function getWeekStart(dateInput) {
  const date = new Date(dateInput);
  const weekday = date.getDay();
  return addDays(date, weekday === 0 ? -6 : 1 - weekday);
}

export function getBlockWeeks(block) {
  const weeks = [];
  for (let monday = getWeekStart(parseISODate(block.start)); dateToISO(monday) <= block.end; monday = addDays(monday, 7)) {
    weeks.push(monday);
  }
  return weeks;
}

function minutesNow(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function isSchedulable(event) {
  return Number.isFinite(event.startMinutes) && !["holiday", "notice", "tp", "placeholder", "seminar"].includes(event.kind);
}

export function getNowState(daySchedule, nowInput = new Date()) {
  const now = new Date(nowInput);
  const currentMinutes = minutesNow(now);
  const timedEvents = daySchedule.events.filter(isSchedulable);
  const current = timedEvents.find((event) => currentMinutes >= event.startMinutes && currentMinutes < event.endMinutes) || null;
  const next = timedEvents.find((event) => event.startMinutes > currentMinutes) || null;

  if (current) {
    return { status: "current", current, next, minutesRemaining: current.endMinutes - currentMinutes };
  }
  if (next) return { status: "next", current: null, next, minutesRemaining: null };
  if (timedEvents.length) return { status: "done", current: null, next: null, minutesRemaining: null };
  return { status: "empty", current: null, next: null, minutesRemaining: null };
}

// Tudo abaixo depende dos dados de UM período específico (síndromes, blocos, grade,
// ambulatórios, catálogo de optativas) — por isso vira uma fábrica: cada período
// registrado em ./periods/index.js recebe sua própria instância, com os mesmos nomes de
// função de sempre, só que fechados sobre o dataset daquele período. HorarioPage.js troca
// de instância quando o usuário troca de período no perfil.
export function createScheduleEngine(dataset) {
  const { syndromes, blocks, defaultRotations, baseSchedule, fixedClinics, specialtyClinics, extraCatalog } = dataset;
  const syndromeKeys = Object.keys(syndromes);

  const inRange = (value, min, max, fallback) => {
    const numericValue = Number(value);
    return Number.isInteger(numericValue) && numericValue >= min && numericValue <= max ? numericValue : fallback;
  };

  function getBlockForDate(date) {
    const isoDate = dateToISO(date);
    return blocks.find((block) => isoDate >= block.start && isoDate <= block.end) || null;
  }

  function getAcademicState(date) {
    const isoDate = dateToISO(date);
    const currentBlock = getBlockForDate(date);
    if (currentBlock) return { phase: "block", currentBlock, previousBlock: null, nextBlock: null };

    const nextBlock = blocks.find((block) => block.start > isoDate) || null;
    const previousBlock = [...blocks].reverse().find((block) => block.end < isoDate) || null;
    if (!previousBlock) return { phase: "before", currentBlock: null, previousBlock: null, nextBlock };
    if (!nextBlock) return { phase: "after", currentBlock: null, previousBlock, nextBlock: null };
    return { phase: "between", currentBlock: null, previousBlock, nextBlock };
  }

  function getBlockProgress(date, block = getBlockForDate(date)) {
    if (!block) return null;
    const totalDays = calendarDaysBetween(block.start, block.end) + 1;
    const elapsedDays = Math.min(totalDays, Math.max(1, calendarDaysBetween(block.start, dateToISO(date)) + 1));
    const remainingDays = Math.max(0, totalDays - elapsedDays);
    return {
      totalDays,
      elapsedDays,
      remainingDays,
      percentage: Math.round((elapsedDays / totalDays) * 100)
    };
  }

  function createDefaultProfile(letter = "A", group = 3) {
    const safeLetter = ["A", "B", "C"].includes(letter) ? letter : "A";
    const safeGroup = Math.min(4, Math.max(1, Number(group) || 1));
    return {
      schemaVersion: 2,
      configured: false,
      letter: safeLetter,
      groupMode: "same",
      sameGroup: safeGroup,
      groups: Object.fromEntries(syndromeKeys.map((key) => [key, safeGroup])),
      clinics: {
        fixedGroup: safeGroup,
        aeReumato: safeGroup,
        aeCirurgicas: safeGroup,
        aeObstetricia: safeGroup,
        specialtyGroups: Object.fromEntries(syndromeKeys.map((key) => [key, safeGroup]))
      },
      rotation: { ...defaultRotations[safeLetter] },
      extraActivities: []
    };
  }

  function normalizeProfile(input = {}) {
    if (!input || typeof input !== "object") input = {};
    const letter = ["A", "B", "C"].includes(input.letter) ? input.letter : "A";
    const defaults = createDefaultProfile(letter, input.sameGroup);
    const fixedMaximum = letter === "A" ? 6 : 5;
    const profile = {
      ...defaults,
      configured: Boolean(input.configured),
      groupMode: input.groupMode === "custom" ? "custom" : "same",
      sameGroup: inRange(input.sameGroup, 1, 4, defaults.sameGroup),
      groups: Object.fromEntries(syndromeKeys.map((key) => [key, inRange(input.groups?.[key], 1, 4, defaults.groups[key])])),
      clinics: {
        fixedGroup: inRange(input.clinics?.fixedGroup, 1, fixedMaximum, Math.min(defaults.clinics.fixedGroup, fixedMaximum)),
        aeReumato: inRange(input.clinics?.aeReumato, 1, 5, defaults.clinics.aeReumato),
        aeCirurgicas: inRange(input.clinics?.aeCirurgicas, 1, 5, defaults.clinics.aeCirurgicas),
        aeObstetricia: inRange(input.clinics?.aeObstetricia, 1, 9, defaults.clinics.aeObstetricia),
        specialtyGroups: Object.fromEntries(syndromeKeys.map((key) => [key, inRange(input.clinics?.specialtyGroups?.[key], 1, 9, defaults.clinics.specialtyGroups[key])]))
      },
      rotation: { ...defaultRotations[letter] },
      extraActivities: normalizeExtraActivities(input.extraActivities)
    };

    for (const blockNumber of [2, 3, 4]) {
      const syndrome = input.rotation?.[blockNumber];
      if (syndromes[syndrome]) profile.rotation[blockNumber] = syndrome;
    }

    // Migrate v1 without changing any existing classroom or clinic assignments.
    // In v2 clinics are always independent of the classroom subgroup.
    if (input.schemaVersion !== 2 && profile.groupMode === "same") {
      profile.groups = Object.fromEntries(syndromeKeys.map((key) => [key, profile.sameGroup]));
      profile.clinics.fixedGroup = Math.min(profile.sameGroup, fixedMaximum);
      profile.clinics.aeReumato = profile.sameGroup;
      profile.clinics.aeCirurgicas = profile.sameGroup;
      profile.clinics.aeObstetricia = profile.sameGroup;
    }

    profile.groupMode = "custom";

    return profile;
  }

  function normalizeExtraActivities(input) {
    if (!Array.isArray(input)) return [];
    const usedIds = new Set();
    return input.flatMap((item, index) => {
      if (!item || !["elective", "extension"].includes(item.kind)) return [];
      const title = String(item.title || "").trim().slice(0, 240);
      const weekday = Number(item.weekday);
      const startMinutes = timeToMinutes(item.start);
      const endMinutes = timeToMinutes(item.end);
      const catalog = extraCatalog.find((entry) => entry.id === item.catalogId && entry.kind === item.kind);
      const startDate = item.startDate || "2026-07-28";
      const endDate = item.endDate || "2026-12-18";
      if (!title || !Number.isInteger(weekday) || weekday < 1 || weekday > 5 || startMinutes === null || endMinutes === null || endMinutes <= startMinutes || !validDate(startDate) || !validDate(endDate) || startDate > endDate) return [];
      let id = String(item.id || `extra-${index}`).slice(0, 100);
      while (usedIds.has(id)) id += `-${index}`;
      usedIds.add(id);
      return [{ id, kind: item.kind, title, weekday, start: item.start, end: item.end || "", startDate, endDate,
        catalogId: catalog?.id || "", note: catalog?.note || "", reference: catalog?.reference || "",
        location: String(item.location || "").trim().slice(0, 160), professor: String(item.professor || "").trim().slice(0, 100) }];
    });
  }

  function getFixedClinic(profile, blockNumber) {
    return fixedClinics[blockNumber]?.[profile.letter]?.[profile.clinics.fixedGroup] || null;
  }

  function getSpecialtyClinics(profile, syndrome, blockNumber) {
    if (blockNumber < 1 || blockNumber > 4) return [];
    if (dataset.specialtyClinicBlocks && !dataset.specialtyClinicBlocks.includes(blockNumber)) return [];
    if (!['reumato', 'cirurgicas', 'obstetricia'].includes(syndrome)) {
      return specialtyClinics[syndrome]?.[profile.clinics.specialtyGroups[syndrome]] || [];
    }
    if (blockNumber === 1) return [];
    if (syndrome === "reumato") return specialtyClinics.reumato?.[profile.clinics.aeReumato] || [];
    if (syndrome === "cirurgicas") return specialtyClinics.cirurgicas?.[profile.clinics.aeCirurgicas] || [];
    if (syndrome === "obstetricia") return specialtyClinics.obstetricia?.[profile.clinics.aeObstetricia] || [];
    return [];
  }

  function selectedClinicsForDay(profile, syndrome, blockNumber, weekday) {
    const fixedClinic = getFixedClinic(profile, blockNumber);
    const specialty = getSpecialtyClinics(profile, syndrome, blockNumber);
    return [fixedClinic, ...specialty].filter((event) => event && event.day === weekday);
  }

  function getDaySchedule(dateInput, profileInput, options = {}) {
    const date = new Date(dateInput);
    const profile = normalizeProfile(profileInput);
    const isoDate = dateToISO(date);
    const weekday = date.getDay();
    const block = getBlockForDate(date);
    const syndrome = block ? getSyndromeForProfile(profile, block.number) : null;
    // A navegação semanal usa datas reais e mantém os avisos de cada dia.
    const notice = options.ignoreCalendarNotices ? null : ACADEMIC_EVENTS[isoDate] || null;
    const extras = extraEventsForDay(date, profile);

    if (notice?.noClasses || (notice?.specialSchedule && weekday === 6)) {
      return specialDaySchedule(date, notice, block, syndrome);
    }

    if (!block) {
      const academicState = getAcademicState(date);
      const gapMessage = academicState.phase === "between" ? "Intervalo entre blocos" : academicState.phase === "before" ? "Semestre ainda não iniciado" : "Blocos temáticos encerrados";
      return { date, block: null, syndrome: null, group: null, notice, events: combineEvents([], extras), dataWarning: gapMessage };
    }

    if (dataset.incompleteBlocks?.includes(block.number)) {
      return { date, block, syndrome, group: null, notice, events: combineEvents([], extras), dataWarning: 'Falta o PDF de Clínicas deste bloco.' };
    }

    if (weekday === 0 || weekday === 6) {
      return { date, block, syndrome, group: null, notice, events: [], dataWarning: "Fim de semana" };
    }

    if (!syndrome || !baseSchedule[syndrome]) {
      return {
        date,
        block,
        syndrome: null,
        group: null,
        notice,
        events: combineEvents([], extras),
        dataWarning: block.number === 1 ? "A grade enviada detalha as rotações dos blocos 2, 3 e 4." : "Defina a síndrome deste bloco no perfil."
      };
    }

    const group = profile.groups[syndrome];
    const dayBase = dataset.baseScheduleByLetter?.[syndrome]?.[profile.letter]?.[weekday] || baseSchedule[syndrome][weekday] || {};
    const clinicEvents = selectedClinicsForDay(profile, syndrome, block.number, weekday);
    const events = SLOT_DEFS.map((slot) => {
      const baseEvent = dayBase[slot.id]?.[group] || null;
      // A escala de especialidades reúne atendimentos de vários blocos. Uma
      // linha da escala só ocupa este bloco quando a grade abre espaço para AE;
      // por exemplo, END terça de manhã não substitui Seminários/PE de Neuro.
      const overlays = clinicEvents.filter((clinicEvent) => clinicEvent.period === slot.period &&
        (clinicEvent.source !== "specialty" || !baseEvent || baseEvent.kind === "tp" ||
          (baseEvent.kind === "placeholder" && baseEvent.accepts?.includes("specialty"))));
      return attachSlot(resolveSlot(baseEvent, overlays), slot);
    });
    // Campo ausente vale para qualquer valor: prova sem `group` vale para a turma inteira.
    const dated = (dataset.datedEvents?.[isoDate] || [])
      .filter((event) => event.block === block.number && event.syndrome === syndrome &&
        event.letter === profile.letter && (event.group === undefined || event.group === group));
    // Prova acontece no horário da própria aula: a aula com o código `replaces` vira a prova.
    const exams = dated.filter((event) => event.type === 'exam');
    const scheduled = mergeConsecutiveEvents(events).map((item) => {
      const exam = exams.find((candidate) => candidate.replaces === item.code);
      return exam ? { ...item, exam: true, examOf: item.code, title: exam.title, code: 'PROVA',
        examDetail: (exam.points ? exam.points + ' pts · ' : '') + exam.detail } : item;
    });
    const datedEvents = dated.filter((event) => event.type !== 'exam')
      .map((event) => {
        const preceding = event.after ? scheduled.find((item) => (item.examOf || item.code) === event.after) : null;
        const startMinutes = event.at ? timeToMinutes(event.at) : preceding?.endMinutes ?? null;
        const start = Number.isFinite(startMinutes)
          ? `${String(Math.floor(startMinutes / 60)).padStart(2, '0')}:${String(startMinutes % 60).padStart(2, '0')}` : '';
        return { code: 'SEMINÁRIO', title: event.title, professor: event.professor,
          kind: 'seminar', source: 'period', start, startMinutes, end: '', endMinutes: null,
          after: event.after || null, inferred: Boolean(event.inferred),
          timeLabel: event.after ? `Após ${start || event.after}${event.inferred ? ' · posição inferida' : ''}` : start,
          slotIds: [], period: 'point' };
      });

    return {
      date,
      block,
      syndrome,
      group,
      notice,
      events: combineEvents([...scheduled, ...datedEvents], extras),
      dataWarning: ""
    };
  }

  function getWeekSchedule(dateInput, profile, options = {}) {
    const monday = getWeekStart(dateInput);
    return Array.from({ length: 5 }, (_, index) => getDaySchedule(addDays(monday, index), profile, options));
  }

  function findNextEvent(nowInput, profile, maximumDays = 21) {
    const now = new Date(nowInput);
    for (let offset = 0; offset <= maximumDays; offset += 1) {
      const candidateDate = addDays(now, offset);
      const schedule = getDaySchedule(candidateDate, profile);
      const minimumStart = offset === 0 ? minutesNow(now) : -1;
      const event = schedule.events.find((candidate) => isSchedulable(candidate) && candidate.startMinutes > minimumStart);
      if (event) return { ...event, date: candidateDate, syndrome: schedule.syndrome, block: schedule.block };
    }
    return null;
  }

  function getProfileSummary(profileInput, blockNumber = null) {
    const profile = normalizeProfile(profileInput);
    const syndrome = blockNumber ? getSyndromeForProfile(profile, blockNumber) : null;
    return {
      label: `${profile.letter}${syndrome ? profile.groups[syndrome] : profile.sameGroup}`,
      letter: profile.letter,
      group: syndrome ? profile.groups[syndrome] : profile.sameGroup,
      syndrome,
      fixed: `${profile.letter}-AF${profile.clinics.fixedGroup}`,
      ae: syndrome === "obstetricia" ? `AE${profile.clinics.aeObstetricia}` : syndrome === "cirurgicas" ? `AE${profile.clinics.aeCirurgicas}` : syndrome === "reumato" ? `AE${profile.clinics.aeReumato}` : `AE${profile.clinics.specialtyGroups[syndrome] || profile.sameGroup}`
    };
  }

  return {
    getBlockForDate,
    getAcademicState,
    getBlockProgress,
    createDefaultProfile,
    normalizeProfile,
    normalizeExtraActivities,
    getDaySchedule,
    getWeekSchedule,
    findNextEvent,
    getProfileSummary
  };
}
